/**
 * @fileoverview Syndicator Showdown — Share My Stable Button
 * @module components/share/ShareStableButton
 *
 * Lives inside the My Stable entry detail panel. Builds a privacy-safe
 * snapshot of the given entry (see stableShareService.buildStableSnapshot),
 * publishes it to stable_shares/{shareId}, and opens the QR/share modal.
 *
 * Default behaviour: while entries are still open, runner selections stay
 * hidden on the public page (revealMode "after_entries_close"). The modal
 * offers an explicit escalation ("reveal my selections now") gated behind a
 * confirmation, matching the Phase 1 spec.
 */

import React, { useState, useCallback } from 'react';
import {
  doc, getDoc, getDocs, collection, query, orderBy,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  buildStableSnapshot, createStableShare, revealStableShareNow, getMyShareStats,
} from '../../services/stableShareService';
import { buildStableShareUrl } from '../../utils/shareLinks';
import { buildStableCardMetrics } from '../../utils/stableStatus';
import { getPublicName, getProfileImageUrl } from '../../utils/profileImage';
import { buildStableShareText } from '../../utils/shareCopy';
import ShareQrModal from './ShareQrModal';
import ShareStableCard from './ShareStableCard';
import './ShareStableButton.css';

function toMs(val) {
  if (!val) return null;
  if (typeof val === 'number') return val;
  if (typeof val.toMillis === 'function') return val.toMillis();
  if (val._seconds !== undefined) return val._seconds * 1000;
  const ms = new Date(val).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function computeEntriesClosed(slate) {
  if (!slate) return false;
  if (slate.finalised) return true;
  const closeMs = toMs(slate.gameCloseTime);
  return !!closeMs && Date.now() >= closeMs;
}

export default function ShareStableButton({
  entry,
  user,
  className = 'ms-action-btn ms-action-btn--outline',
  label = '🔗 Share My Stable',
  compact = false,
}) {
  const [step, setStep]               = useState('idle'); // idle | loading | ready | error
  const [shareId, setShareId]         = useState(null);
  const [revealMode, setRevealMode]   = useState(null);
  const [entriesClosed, setEntriesClosed] = useState(false);
  const [qrOpen, setQrOpen]           = useState(false);
  const [cardProps, setCardProps]     = useState(null);
  const [shareStats, setShareStats]   = useState(null);

  const notSubmitted = entry?.status !== 'submitted';

  const handleShareClick = useCallback(async () => {
    if (notSubmitted || !user) return;

    if (shareId) { setQrOpen(true); return; } // reuse the share already created this session

    setStep('loading');
    try {
      const userSnap = await getDoc(doc(db, 'users', user.uid));
      const userDoc  = userSnap.exists() ? userSnap.data() : null;

      const slateSnap = await getDoc(doc(db, 'game_slates', entry.slateId));
      const slate = slateSnap.exists() ? { id: slateSnap.id, ...slateSnap.data() } : null;

      const racesSnap = await getDocs(
        query(collection(db, 'game_slates', entry.slateId, 'races'), orderBy('raceNumber', 'asc'))
      );
      const races = racesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const allocSnap = await getDocs(collection(db, 'player_entries', entry.id, 'race_allocations'));
      const allocations = {};
      allocSnap.docs.forEach((d) => { allocations[d.id] = d.data(); });

      const closedNow = computeEntriesClosed(slate);
      const initialRevealMode = closedNow ? 'immediate' : 'after_entries_close';
      const snapshot = buildStableSnapshot({ slate, entry, allocations, races });
      const metrics = buildStableCardMetrics(entry, races);

      const newShareId = await createStableShare({
        user, userDoc, slateId: entry.slateId, revealMode: initialRevealMode, snapshot,
      });

      setShareId(newShareId);
      setRevealMode(initialRevealMode);
      setEntriesClosed(closedNow);
      setCardProps({
        avatarUrl:    getProfileImageUrl(userDoc, user),
        stableName:   getPublicName(userDoc, user),
        showdownName: snapshot.gameName,
        statusLabel:  metrics.statusLabel,
        resultedRaceCount: metrics.resultedRaceCount,
        totalRaceCount:    metrics.totalRaces,
        virtualWinnings:   metrics.virtualWinnings,
        rank:         entry.finalRank ?? null,
        totalRunners: snapshot.totalRunners,
      });
      setStep('ready');
      setQrOpen(true);

      // Best-effort — "Your stable link has been viewed N times" feedback,
      // shown once the fresh share above is already reflected in the count.
      getMyShareStats(user.uid).then(setShareStats);
    } catch (err) {
      console.error('[ShareStableButton] Failed to create stable share:', err.code, err.message, err);
      setStep('error');
    }
  }, [notSubmitted, user, shareId, entry]);

  const handleRevealNow = useCallback(async () => {
    if (!shareId) return;
    const confirmed = window.confirm(
      'Other players will be able to see your exact runner selections and equity ' +
      'before this Showdown closes. They could copy your picks. Are you sure you ' +
      'want to reveal your selections now?'
    );
    if (!confirmed) return;
    try {
      await revealStableShareNow(shareId);
      setRevealMode('immediate');
    } catch (err) {
      console.error('[ShareStableButton] revealStableShareNow error:', err.code, err.message, err);
    }
  }, [shareId]);

  const shareUrl = shareId ? buildStableShareUrl(shareId) : null;

  const revealNote = entriesClosed ? (
    <p className="ssb-reveal-text ssb-reveal-text--closed">
      ✓ Entries have closed — your full stable is visible on this page.
    </p>
  ) : revealMode === 'immediate' ? (
    <p className="ssb-reveal-text ssb-reveal-text--closed">
      ✓ Full selections are visible on this page.
    </p>
  ) : (
    <div className="ssb-reveal-note">
      <p className="ssb-reveal-text">
        Your exact runner selections are hidden until entries close.
      </p>
      <button type="button" className="ssb-reveal-btn" onClick={handleRevealNow}>
        Reveal My Full Selections Now
      </button>
      <p className="ssb-reveal-warning">
        ⚠️ Other players will be able to see your selections before the game closes.
      </p>
    </div>
  );

  const footerNote = (
    <>
      {revealNote}
      {shareStats && shareStats.shareCount > 0 && (
        <p className="ssb-share-stats">
          Shared {shareStats.shareCount} time{shareStats.shareCount === 1 ? '' : 's'} · {shareStats.totalViews} view{shareStats.totalViews === 1 ? '' : 's'}
        </p>
      )}
    </>
  );

  return (
    <div className={`ssb-wrap ${compact ? 'ssb-wrap--compact' : ''}`}>
      <button
        type="button"
        className={className}
        onClick={handleShareClick}
        disabled={notSubmitted || step === 'loading'}
        title={notSubmitted ? 'Submit your stable before sharing full selections.' : undefined}
      >
        {step === 'loading' ? 'Preparing…' : label}
      </button>

      {!compact && notSubmitted && (
        <p className="ssb-hint">Submit your stable before sharing full selections.</p>
      )}
      {step === 'error' && (
        <p className="ssb-hint ssb-hint--error">
          Couldn't create a share link — please try again.
        </p>
      )}

      {shareUrl && (
        <ShareQrModal
          isOpen={qrOpen}
          onClose={() => setQrOpen(false)}
          url={shareUrl}
          shareId={shareId}
          title="Share My Stable"
          description="Anyone with this link can see a summary of your stable — no login required."
          shareTitle="Check out my stable on Syndicator Showdown"
          shareText={cardProps ? buildStableShareText(cardProps) : 'I\'ve built my racing stable for this Showdown — come check it out.'}
          previewComponent={cardProps && <ShareStableCard {...cardProps} compact />}
          footerNote={footerNote}
        />
      )}
    </div>
  );
}
